import { z } from "zod";
import { api } from "@/lib/api/client";
import { guardarNonce, htuDe, montarProva } from "@/lib/auth/dpop/prova";

/**
 * O contrato DPoP do socket (faixa 33 §6): payload do handshake e do `auth:renew`, leitura das recusas. Quando usar é
 * do `socket.ts`. Ver `socketDpop.md`.
 */

/**
 * Prova DPoP do socket (faixa 33 §6): um `GET` em `<API>/socket.io/` com `ath` do token. A base é a do axios
 * (`api.defaults.baseURL` = `NEXT_PUBLIC_API_URL`, B3), NUNCA o `NEXT_PUBLIC_SOCKET_URL` (`wss://`). Sem prova
 * (chave falhou, sem suporte, base inválida) → `null`: vai só o token, e o back recusa se a sessão é presa.
 */
const provaDoSocket = async (token: string): Promise<string | null> => {
  const htu = htuDe(api.defaults.baseURL, "/socket.io/");
  if (htu === null) return null;
  const resultado = await montarProva({ htm: "GET", htu, accessToken: token }).catch(() => null);
  return resultado?.tipo === "prova" ? resultado.prova : null;
};

/** Payload do `auth`/`auth:renew`: sessão DPoP leva `dpop` (prova nova a cada chamada, B2). */
export const credencialDoSocket = async (token: string): Promise<{ token: string; dpop?: string }> => {
  const dpop = await provaDoSocket(token);
  return dpop ? { token, dpop } : { token };
};

/** `err.data` da recusa DPoP do handshake (back `connect.py::_exigir_dpop`): `{code, nonce}`. Fora disso, `null`. */
const RecusaDpopSchema = z.object({ code: z.string(), nonce: z.string().optional() });

export const recusaDpopDo = (err: unknown): z.infer<typeof RecusaDpopSchema> | null => {
  const dados = typeof err === "object" && err !== null ? (err as { data?: unknown }).data : undefined;
  const lida = RecusaDpopSchema.safeParse(dados);
  return lida.success ? lida.data : null;
};

/** As recusas DPoP do `auth:renew` trazem o nonce novo (faixa 33 §6). */
export const guardarNonceDoAck = (ack: unknown): void => {
  if (typeof ack !== "object" || ack === null) return;
  const { nonce } = ack as { nonce?: unknown };
  if (typeof nonce === "string") guardarNonce(nonce);
};
